import type { TourPath } from '@pantry/shared';
import { getTourById } from '@pantry/api-client';
import { getPathFilteredSteps, getStepsBeforeIndex } from './tour-context.helpers';

/** Wait for Driver.js popover DOM to render before manipulating footer buttons */
export const POPOVER_RENDER_DELAY = 50;
/** Wait for driver destroy + reinit during path switch */
export const PATH_SWITCH_DELAY = 100;
/**
 * Wait for React route component to mount after navigation.
 * This is layer 1 of 2: driveTourStepWhenReady adds up to 1000ms of element
 * polling on top, giving a combined budget of ~1500ms for slow renders.
 */
export const NAVIGATION_SETTLE_DELAY = 500;
/** Brief pause between chained tours so the transition feels intentional */
export const HANDOFF_DELAY = 500;
/** Wait for onHighlighted DOM update before modifying popover content */
export const HIGHLIGHT_SETTLE_DELAY = 10;

/**
 * Resolve a navigateTo value to an actual route.
 * Special values like 'firstHouseholdSettings' are resolved to actual paths.
 * Returns null if the special value can't be resolved (e.g., no household exists).
 */
export function resolveNavigateTo(
  navigateTo: string,
  firstHouseholdId: string | null
): string | null {
  if (navigateTo === 'firstHouseholdSettings') {
    if (firstHouseholdId) {
      return `/households/${firstHouseholdId}`;
    }
    return null;
  }
  return navigateTo;
}

/**
 * Resolve the target route for starting or resuming a tour.
 * Shared between startTour and resumeTour to avoid duplicating route resolution logic.
 */
export function resolveStartRoute(options: {
  tourId: string;
  fullStepIndex: number;
  driverStepIndex: number;
  resolvedPath: TourPath | null;
  firstHouseholdId: string | null;
  currentPathname: string;
}): string {
  const {
    tourId,
    fullStepIndex,
    driverStepIndex,
    resolvedPath,
    firstHouseholdId,
    currentPathname,
  } = options;
  const definition = getTourById(tourId);

  let targetRoute: string | null = null;

  // For resumed tours, find last resolvable navigateTo from previous steps
  if (fullStepIndex > 0) {
    const stepsBeforeIndex = getStepsBeforeIndex(definition, fullStepIndex, resolvedPath);
    for (let i = stepsBeforeIndex.length - 1; i >= 0; i--) {
      const step = stepsBeforeIndex[i];
      if (step?.navigateTo) {
        const resolved = resolveNavigateTo(step.navigateTo, firstHouseholdId);
        if (resolved) {
          targetRoute = resolved;
          break;
        }
      }
    }
  }

  // If the current step target is already visible on this page, stay put.
  const filteredSteps = getPathFilteredSteps(definition, resolvedPath);
  const currentStep = filteredSteps[driverStepIndex];
  const currentStepElementExists = !!(
    currentStep?.target &&
    typeof document !== 'undefined' &&
    document.querySelector(currentStep.target)
  );

  if (!targetRoute && currentStepElementExists) {
    targetRoute = currentPathname;
  }

  // For dynamic tours, prefer the current step's navigateTo when available.
  if (!targetRoute && currentStep?.navigateTo) {
    targetRoute = resolveNavigateTo(currentStep.navigateTo, firstHouseholdId);
  }

  // Default to trigger route or dashboard
  if (!targetRoute) {
    const triggerRoute = definition?.triggerRoute;
    const resolvedTriggerRoute = triggerRoute
      ? resolveNavigateTo(triggerRoute, firstHouseholdId)
      : null;
    targetRoute = resolvedTriggerRoute || triggerRoute || '/';
  }

  return targetRoute;
}
